import { useState } from 'react';
import { useFirestore } from '../../../firebase/useFirestore';
import { db } from '../../../firebase/config.js';
import useAutosaveField from '../../../hooks/useAutosaveField';
import AutosaveStatus from '../../common/AutosaveStatus';
import ToggleButton from '../../common/ToggleButton';
import Table from '../../common/table/Table.jsx';
import TableCell from '../../common/table/TableCell.jsx';
import resolveWervingsDiensten, { newDienstKey } from '../../../helpers/wervingsacties/resolveWervingsDiensten';

/** Ruim voor een eigen woord, krap genoeg voor een knop in het registratievenster. */
const MAX_DIENST_LENGTH = 30;

/** Het veld op CompagnyMeta: een map per dienstsleutel. */
const FIELD = 'WervingsDiensten';

/**
 * Instellingen → Ondersteuning → 'Diensten': waarmee een huishouden geholpen
 * wordt (fixer, energiecoach, een eigen partner). Eén lijst voor de kolom
 * 'DIENSTEN' bij huishoudens en voor de registratie van een wervingsactie
 * (sinds 2026-10-13). Per dienst een eigen naam en 'Verbergen'.
 *
 * Nodig naast AdressLabelsConfig, dat dezelfde opslag gebruikt maar ingebouwde
 * labels en een kleur kent; diensten hebben geen van beide.
 *
 * Verbergen en niet verwijderen: een registratierij en een huishouden dragen de SLEUTEL, en die
 * blijft staan. Een verborgen dienst is hier nog te zien en weer aan te zetten.
 * AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10 (2))
 * AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md
 *
 * @returns {JSX.Element}
 */
const WervingsDienstenConfig = () => {
    const [adding, setAdding] = useState(false);
    const [fout, setFout] = useState('');
    const compagny = useFirestore('CompagnyMeta');
    const { save, status } = useAutosaveField('CompagnyMeta');

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const rows = comp ? resolveWervingsDiensten(comp, { includeHidden: true }) : [];

    /** @param {string} key @param {string} value */
    const labelHandler = (key, value) => save(comp.docid, { [`${FIELD}.${key}.Label`]: value });

    // Een knopklik, geen toetsaanslag: direct schrijven, zodat de rij meteen
    // verschijnt. Eén dienst per klik, ook bij een dubbele klik: een dienst is
    // alleen te verbergen, dus een tweede rij blijft voor altijd staan.
    const addHandler = async () => {
        if (adding) return;
        setAdding(true);
        setFout('');
        try {
            await db.collection('CompagnyMeta').doc(comp.docid).update({
                [`${FIELD}.${newDienstKey()}`]: { Label: 'Nieuwe dienst', Position: rows.length + 1, Hidden: 'false' },
            });
        } catch (err) {
            setFout(err?.message || String(err));
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="dashboard-container">
            <h2>Diensten</h2>
            <p>
                Waarmee een huishouden geholpen wordt. Je zet ze op een huishouden in de kolom 'DIENSTEN', en bij
                een wervingsactie vink je ze aan als een bewoner aan de deur 'ja' zegt.
            </p>
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                <>
                    <AutosaveStatus status={status} reserve />
                    {rows.length === 0 ? (
                        <p className="wa-empty">Nog geen diensten. Voeg er een toe.</p>
                    ) : (
                        <Table
                            columns={[
                                { key: 'label', label: 'NAAM', searchable: false },
                                { key: 'hidden', label: 'VERBERGEN', searchable: false },
                            ]}
                            items={rows}
                            getItemKey={(row) => row.key}
                            showSearch={false}
                            tableClassName="status-config-table"
                            getRowClassName={(row) => (row.hidden ? 'adress-labels-config__row--hidden' : '')}
                            renderRow={(row) => (
                                <>
                                    <TableCell>
                                        {/* Ongecontroleerd veld met defaultValue, zie AdressLabelsConfig.
                                            AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus */}
                                        <input
                                            type="text"
                                            maxLength={MAX_DIENST_LENGTH}
                                            defaultValue={row.label}
                                            onChange={(e) => labelHandler(row.key, e.target.value)}
                                            aria-label={row.label ? `Naam van de dienst '${row.label}'` : 'Naam van een dienst zonder naam'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <ToggleButton
                                            docid={comp.docid}
                                            id={`${FIELD}.${row.key}.Hidden`}
                                            collection={'CompagnyMeta'}
                                            value={comp[FIELD]?.[row.key]?.Hidden}
                                        />
                                    </TableCell>
                                </>
                            )}
                        />
                    )}
                    <button type="button" className="adress-labels-config__add" onClick={addHandler} disabled={adding}>
                        + Dienst toevoegen
                    </button>
                    {fout && <p className="wa-error" role="alert">{`Toevoegen is niet gelukt (${fout}). Probeer het opnieuw.`}</p>}
                </>
            )}
        </div>
    );
};

export default WervingsDienstenConfig;
